import { useState } from 'react'
import { useWriteContract, useWaitForTransactionReceipt, useChainId, useAccount } from 'wagmi'
import { getNFTContract, parseContractError } from '@/config/contract'
import toast from 'react-hot-toast'
import { defaultChainId, isSupportedNetwork } from '@/config/wagmi'

export interface RefundState {
  isLoading: boolean
  isConfirming: boolean
  isSuccess: boolean
  error: string | null
  transactionHash: string | null
}

export const useRefund = () => {
  const { isConnected } = useAccount()
  const chainIdFromWallet = useChainId()

  const chainId = isConnected && isSupportedNetwork(chainIdFromWallet || 0)
    ? (chainIdFromWallet as number)
    : defaultChainId
  const contract = getNFTContract(chainId)
  const { writeContract, data: hash, reset } = useWriteContract()

  // Explicit gas limit for refund to keep estimates sane on Sepolia/BSC
  const refundGasLimit: bigint = chainId === 31337 ? 5_000_000n : 400_000n
  
  const [state, setState] = useState<RefundState>({
    isLoading: false,
    isConfirming: false,
    isSuccess: false,
    error: null,
    transactionHash: null,
  })

  // Watch for transaction confirmation
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  /**
   * Request refund for a token
   */
  const refund = async (tokenId: number) => {
    // Reset previous state
    reset()
    setState({
      isLoading: true,
      isConfirming: false,
      isSuccess: false,
      error: null,
      transactionHash: null,
    })

    try {
      // Show transaction prompt
      toast.loading('Please confirm the refund transaction in your wallet...', {
        id: 'refund-prompt',
      })

      // Execute refund transaction
      writeContract(
        {
          ...contract,
          functionName: 'refundNFT',
          args: [BigInt(tokenId)],
          gas: refundGasLimit,
        },
        {
          onError: (error) => {
            console.error('❌ Refund error:', error)
            toast.dismiss('refund-prompt')
            const errorMessage = parseContractError(error as Error)
            setState((prev) => ({
              ...prev,
              isLoading: false,
              error: errorMessage,
            }))
            toast.error(errorMessage)
          },
          onSuccess: () => {
            toast.dismiss('refund-prompt')
            toast.loading('Refund submitted! Waiting for confirmation...', {
              id: 'refund-confirming',
            })
            setState((prev) => ({
              ...prev,
              isLoading: false,
              isConfirming: true,
            }))
          },
        }
      )
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process refund'
      setState({
        isLoading: false,
        isConfirming: false,
        isSuccess: false,
        error: errorMessage,
        transactionHash: null,
      })
      toast.error(errorMessage)
    }
  }

  // Update state when transaction is confirmed
  if (isSuccess && !state.isSuccess) {
    toast.dismiss('refund-confirming')
    toast.success('Refund processed successfully! 💰')
    setState((prev) => ({
      ...prev,
      isConfirming: false,
      isSuccess: true,
      transactionHash: hash || null,
    }))
  }

  // Update confirming state
  if (isConfirming && !state.isConfirming && state.isLoading) {
    setState((prev) => ({
      ...prev,
      isConfirming: true,
      isLoading: false,
    }))
  }

  return {
    refund,
    ...state,
    reset: () => {
      reset()
      setState({
        isLoading: false,
        isConfirming: false,
        isSuccess: false,
        error: null,
        transactionHash: null,
      })
    },
  }
}
